import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge   from "@react/shared/ui/Badge"
import Avatar  from "./Avatar"
import { fmt, dateFmt, fmtHoursDays } from "../helpers/format"

// Both rows of the card share this grid, so every column lines up.
const GRID = "grid gap-x-6 gap-y-5 grid-cols-2 md:grid-cols-3 xl:grid-cols-5"

const Field = ({ label, children, strong }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 leading-4">{label}</span>
    <span className={`flex h-6 min-w-0 items-center truncate tabular-nums ${strong ? "text-[15px] font-semibold text-slate-900" : "text-[13px] text-slate-900"}`}>
      {children || <span className="text-slate-400">—</span>}
    </span>
  </div>
)

const SummarySection = ({ project, kpis, hours, documents, editPath, policies }) => {
  const quotes = documents?.quotes || []
  const totals = hours?.totals
  const rate   = hours?.rate
  const perDay = hours?.hours_per_day

  return (
    <section id="section-summary" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.summary.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {I18n.t("projects.summary.subtitle")}
          </p>
        </div>

        {editPath && policies?.can_edit_project !== false && (
          <a
            href={editPath}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
          >
            <FontAwesomeIcon icon="pen" />
            {I18n.t("projects.summary.edit")}
          </a>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-5">
        <div className={GRID}>
          <Field label={I18n.t("projects.client")}>
            {project.client?.path
              ? <a href={project.client.path} className="hover:underline">{project.client.name}</a>
              : project.client?.name}
          </Field>
          <Field label={I18n.t("projects.site")}>
            {project.site?.path
              ? <a href={project.site.path} className="hover:underline">{project.site.name}</a>
              : project.site?.name}
          </Field>
          <Field label={I18n.t("projects.summary.manager")}>
            {project.manager?.name && (
              <span className="inline-flex items-center gap-2">
                <Avatar name={project.manager.name} size="sm" ring={false} />
                {project.manager.name}
              </span>
            )}
          </Field>
          <Field label={I18n.t("projects.summary.period")}>
            {I18n.t("projects.summary.period_range", { start: dateFmt(project.start_date), end: dateFmt(project.end_date) })}
          </Field>
          <Field label={I18n.t("projects.summary.code")}>{project.code}</Field>
          {project.department && (
            <Field label={I18n.t("projects.summary.department")}>{`${project.department.code} · ${project.department.name}`}</Field>
          )}
          {project.trade && (
            <Field label={I18n.t("projects.summary.trade")}>{`${project.trade.code} · ${project.trade.name}`}</Field>
          )}
        </div>

        <div className={`border-t border-slate-100 pt-4 ${GRID}`}>
          <Field label={I18n.t("projects.finance.initial_budget")}>{fmt(kpis?.initial_budget)}</Field>
          <Field label={I18n.t("projects.finance.signed_avenants")}>{fmt(kpis?.signed_avenants)}</Field>
          <Field label={I18n.t("projects.finance.revised_contract")} strong>{fmt(kpis?.revised_contract)}</Field>
          <Field label={I18n.t("projects.completion")}>{`${Math.round(kpis?.completion || 0)} %`}</Field>
          <Field label={I18n.t("projects.invoicing_method")}>
            {project.invoicing_method ? I18n.t(`projects.invoicing_methods.${project.invoicing_method}`) : "—"}
          </Field>
        </div>

        {/* The site's hours and what they are worth, read without opening the Hours tab. */}
        {totals && (
          <div className={`border-t border-slate-100 pt-4 ${GRID}`}>
            <Field label={I18n.t("projects.summary.hours_sold")} strong>
              {totals.sold > 0 ? fmtHoursDays(totals.sold, perDay) : null}
            </Field>
            <Field label={I18n.t("projects.summary.hours_done")} strong>
              {totals.done > 0 ? (
                <span className={totals.sold > 0 && totals.done > totals.sold ? "text-rose-700" : "text-emerald-700"}>{fmtHoursDays(totals.done, perDay)}</span>
              ) : null}
            </Field>
            <Field label={I18n.t("projects.summary.hours_remaining")}>
              {totals.sold > 0 ? fmtHoursDays(totals.remaining, perDay) : null}
            </Field>
            <Field label={I18n.t("projects.summary.hours_done_amount")}>
              {totals.done_amount ? fmt(totals.done_amount) : null}
            </Field>
            <Field label={I18n.t("projects.summary.hour_rate")}>
              {rate?.amount ? (
                <span className="inline-flex items-baseline gap-1.5">
                  {I18n.t("projects.hours.rate.per_hour", { rate: fmt(rate.amount) })}
                  <span className="text-[11px] text-slate-400">{I18n.t(`projects.summary.hour_rate_${rate.source}`)}</span>
                </span>
              ) : null}
            </Field>
          </div>
        )}
      </div>

      <div className="mt-4">
        <h3 className="pl-[16px] text-[15px] font-semibold text-slate-900 mb-3">
          {I18n.t("projects.summary.quotes")} {quotes.length > 0 && <span className="text-slate-400 font-normal">({quotes.length})</span>}
        </h3>

        {quotes.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl px-5 py-6 text-center text-[13px] text-slate-500">
            {I18n.t("projects.summary.no_quotes")}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="min-w-full text-[13px]">
              <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left font-semibold px-5 py-2.5">{I18n.t("projects.summary.table.quote")}</th>
                  <th className="text-left font-semibold px-5 py-2.5">{I18n.t("projects.summary.table.date")}</th>
                  <th className="text-left font-semibold px-5 py-2.5">{I18n.t("projects.status")}</th>
                  <th className="text-right font-semibold px-5 py-2.5">{I18n.t("projects.summary.table.amount")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {/* Toute la ligne mène au devis, comme dans la liste des devis
                    chantier : viser l'intitulé pour ouvrir une pièce est une
                    précision qu'on ne devrait pas demander. */}
                {quotes.map(quote => (
                  <tr
                    key       = {quote.id}
                    onClick   = {() => quote.path && (window.location.href = quote.path)}
                    className = {`hover:bg-slate-50/70 ${quote.path ? "cursor-pointer" : ""}`}
                  >
                    <td className="px-5 py-2.5">
                      <a href={quote.path} className="font-medium text-slate-900 hover:text-blue-700">{quote.title}</a>
                      {quote.avenant_number && (
                        <Badge tone="blue" variant="soft" size="sm" className="ml-2">{quote.avenant_number}</Badge>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-slate-600 tabular-nums">{dateFmt(quote.date)}</td>
                    <td className="px-5 py-2.5">
                      {quote.status_label && <Badge tone="neutral" variant="soft" size="sm">{quote.status_label}</Badge>}
                    </td>
                    <td className="px-5 py-2.5 text-right text-slate-900 font-semibold tabular-nums">{fmt(quote.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default SummarySection
